/**
 * Top-right account menu (click your name/avatar in the header).
 *
 * Polished dropdown showing identity (avatar, name, email, role badge,
 * tenant) with quick links: My Profile, User Guide, GitHub docs, theme
 * toggle, Settings, and a prominent Sign out. Uses the shared useMenu
 * pattern (Escape closes + focus restore, arrow-key navigation,
 * click-outside) and works in light/dark and on narrow viewports.
 */
import React from 'react';
import { ChevronDown, Sun, Moon, LogOut, User, BookOpen, GitBranch, ExternalLink, Settings } from 'lucide-react';
import { cn } from '@hts/ui';
import { useMenu } from '../hooks/useMenu.js';
import type { AuthUser } from '../hooks/useAuth.js';
import { Avatar, ROLE_COLORS, ROLE_LABEL } from '../lib/roles.js';

const DOCS_URL = 'https://github.com/houstontechsys/techsysconnect/tree/main/docs';

export function AccountMenu({
  user,
  activeRole,
  theme,
  onToggleTheme,
  onSignOut,
}: {
  user: AuthUser;
  activeRole: string | undefined;
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
  onSignOut: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  const menu = useMenu(open, React.useCallback(() => setOpen(false), []));
  const close = React.useCallback(() => setOpen(false), []);

  const name = user.name || user.email.split('@')[0];

  return (
    <div className="relative">
      <button
        ref={menu.triggerRef}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-[hsl(var(--surface-subtle))] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Avatar name={user.name} email={user.email} size="xs" />
        <span className="hidden sm:block text-sm font-medium text-[hsl(var(--content))] max-w-[10rem] truncate">
          {name}
        </span>
        <ChevronDown
          className={cn('h-3.5 w-3.5 text-[hsl(var(--content-subtle))] transition-transform duration-150', open && 'rotate-180')}
          aria-hidden="true"
        />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={close} aria-hidden="true" />
          <div
            ref={menu.menuRef}
            role="menu"
            aria-label="Account"
            className="absolute right-0 top-10 z-20 w-72 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-100"
          >
            {/* Identity header */}
            <div className="flex items-center gap-3 px-4 py-3.5 border-b border-[hsl(var(--border))]">
              <Avatar name={user.name} email={user.email} size="md" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[hsl(var(--content))] truncate">{name}</p>
                <p className="text-xs text-[hsl(var(--content-subtle))] truncate">{user.email}</p>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  {activeRole && (
                    <span className={cn('inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-semibold', ROLE_COLORS[activeRole] ?? '')}>
                      {ROLE_LABEL[activeRole] ?? activeRole}
                    </span>
                  )}
                  {user.activeTenant?.name && (
                    <span className="text-[11px] text-[hsl(var(--content-placeholder))] truncate">{user.activeTenant.name}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Menu items */}
            <div className="py-1">
              <MenuItem icon={User} label="My Profile" onClick={close} href="/profile" />
              <MenuItem icon={BookOpen} label="User Guide" onClick={close} href="/USER_GUIDE.pdf" external />
              <MenuItem icon={GitBranch} label="Documentation" onClick={close} href={DOCS_URL} external />
              <MenuItem
                icon={theme === 'dark' ? Sun : Moon}
                label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                onClick={() => { onToggleTheme(); }}
              />
              <MenuItem icon={Settings} label="Settings" onClick={close} href="/admin/settings" />
            </div>

            {/* Sign out — prominent */}
            <div className="border-t border-[hsl(var(--border))] py-1">
              <button
                type="button"
                role="menuitem"
                onClick={() => { close(); onSignOut(); }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
              >
                <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
                Sign out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function MenuItem({
  icon: Icon,
  label,
  onClick,
  href,
  external,
}: {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
  href?: string;
  external?: boolean;
}) {
  const inner = (
    <>
      <Icon className="h-4 w-4 text-[hsl(var(--content-subtle))] shrink-0" aria-hidden="true" />
      <span className="flex-1 text-left">{label}</span>
      {external && <ExternalLink className="h-3.5 w-3.5 text-[hsl(var(--content-placeholder))] shrink-0" aria-hidden="true" />}
    </>
  );
  const cls =
    'w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))] transition-colors focus-visible:outline-none focus-visible:bg-[hsl(var(--surface-subtle))]';
  if (href) {
    return (
      <a role="menuitem" href={href} onClick={onClick} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined} className={cls}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" role="menuitem" onClick={onClick} className={cls}>
      {inner}
    </button>
  );
}
